import { zodTransformFirestoreTimestampToDate } from "../../utils/zodUtils.mjs";
import { zodFlavorId } from "../ids.mjs";
import { defineFirestoreCollection } from "../firebaseCollectionSchema.mjs";
import { z as z$1 } from "zod";

// src/models/carePlan/carePlan.ts
const firestoreDate = z$1.any().transform(zodTransformFirestoreTimestampToDate);
const CARE_PLAN_STATUS = ["ready", "failed"];
const CARE_PLAN_OUTCOME = [
	"generated",
	"unchanged",
	"skipped",
	"failed"
];
const CARE_PLAN_GENERATE_EVENT_TYPE = "care_plan.generate";
const CARE_PLAN_GENERATE_EVENT_SOURCE_CONSULT_RESOLVE = "consult_resolve";
const CARE_PLAN_GENERATE_EVENT_SOURCE_ADMIN = "admin_generate";
/**
* Optional fields on `care_plan.generate` ingress; `{}` is still valid.
* `notifyPatient` is read only for `admin_generate`. System emits omit it
* and always notify.
*/
const carePlanGenerateEventDataSchema = z$1.object({
	actorUid: z$1.string().min(1).optional(),
	notifyPatient: z$1.boolean().optional()
});
/** Hub last-N ready list — same cap as past-list history. */
const CARE_PLAN_HUB_LIST_LIMIT = 30;
/** Unused mint TTL. First successful GET consumes. */
const CARE_PLAN_MINT_TTL_MS = 3e5;
const CARE_PLAN_CONSUME_FUNCTION_NAME = "coord_consumeCarePlanMint";
const CARE_PLAN_PREVIEW_ADMIN_FUNCTION_NAME = "coord_previewCarePlanAdmin";
const CARE_PLAN_PREVIEW_FROM_CANVAS_FUNCTION_NAME = "coord_previewCarePlanFromCanvas";
/** Request path signed in HMAC headers for Canvas → Cloud Function preview POSTs. */
const CARE_PLAN_PREVIEW_FROM_CANVAS_HMAC_PATH = "/";
const CARE_PLAN_DISPOSITION = ["inline", "attachment"];
/** GCS pointer only — never PDF bytes, SOAP, or FHIR. */
const carePlanPointerSchema = z$1.object({
	bucket: z$1.string().min(1),
	object: z$1.string().min(1)
});
/**
* Root `care_plans/{consultationId}` listing + pointer + hash.
* Hub sorts by `scheduledStart`, not `generatedAt`.
*/
const carePlanSchema = z$1.object({
	id: zodFlavorId(),
	customerId: zodFlavorId(),
	scheduledStart: firestoreDate,
	status: z$1.enum(CARE_PLAN_STATUS),
	pointer: carePlanPointerSchema.optional(),
	contentHash: z$1.string().optional(),
	lastOutcome: z$1.enum(CARE_PLAN_OUTCOME),
	generatedAt: firestoreDate.optional(),
	createdAt: firestoreDate,
	updatedAt: firestoreDate
});
const carePlanGenerateRunSchema = z$1.object({
	id: zodFlavorId(),
	eventId: z$1.string().min(1),
	outcome: z$1.enum(CARE_PLAN_OUTCOME),
	reason: z$1.string().optional(),
	completedAt: firestoreDate
});
const CarePlanGenerateRuns = defineFirestoreCollection("generate_runs", carePlanGenerateRunSchema);
const CarePlans = defineFirestoreCollection("care_plans", carePlanSchema, { generate_runs: CarePlanGenerateRuns });
/**
* Top-level `care_plan_mints/{tokenId}` — server-only ledger.
* Not customer-listable. Dual audit with GCS DATA_READ.
*/
const carePlanMintSchema = z$1.object({
	id: zodFlavorId(),
	consultationId: zodFlavorId(),
	customerId: zodFlavorId(),
	mintedByUid: z$1.string().min(1),
	mintedAt: firestoreDate,
	expiresAt: firestoreDate,
	used: z$1.boolean(),
	usedAt: firestoreDate.optional(),
	usedByUid: z$1.string().min(1).optional(),
	requestIp: z$1.string().min(1).optional()
});
const CarePlanMints = defineFirestoreCollection("care_plan_mints", carePlanMintSchema);
const CARE_PLAN_AUDIT_KIND = [
	"preview",
	"generate",
	"download"
];
const CARE_PLAN_AUDIT_CHANNEL = [
	"admin",
	"canvas",
	"consult_resolve",
	"customer"
];
/**
* Top-level `care_plan_audits/{id}` — server-only ledger.
* Metadata only; never PDF, dump, or assembled text (ADR-FN-011).
*/
const carePlanAuditSchema = z$1.object({
	id: zodFlavorId(),
	kind: z$1.enum(CARE_PLAN_AUDIT_KIND),
	at: firestoreDate,
	consultationId: zodFlavorId(),
	canvasNoteId: z$1.string().min(1).optional(),
	channel: z$1.enum(CARE_PLAN_AUDIT_CHANNEL),
	actorUid: z$1.string().min(1).optional(),
	actorEmail: z$1.string().min(1).optional(),
	canvasStaffId: z$1.string().min(1).optional(),
	canvasStaffEmail: z$1.string().min(1).optional(),
	canvasStaffName: z$1.string().min(1).optional(),
	outcome: z$1.string().min(1),
	reason: z$1.string().optional(),
	noteState: z$1.string().optional(),
	disposition: z$1.enum(CARE_PLAN_DISPOSITION).optional(),
	eventId: z$1.string().min(1).optional()
});
const CarePlanAudits = defineFirestoreCollection("care_plan_audits", carePlanAuditSchema);

export { CARE_PLAN_AUDIT_CHANNEL, CARE_PLAN_AUDIT_KIND, CARE_PLAN_CONSUME_FUNCTION_NAME, CARE_PLAN_DISPOSITION, CARE_PLAN_GENERATE_EVENT_SOURCE_ADMIN, CARE_PLAN_GENERATE_EVENT_SOURCE_CONSULT_RESOLVE, CARE_PLAN_GENERATE_EVENT_TYPE, CARE_PLAN_HUB_LIST_LIMIT, CARE_PLAN_MINT_TTL_MS, CARE_PLAN_OUTCOME, CARE_PLAN_PREVIEW_ADMIN_FUNCTION_NAME, CARE_PLAN_PREVIEW_FROM_CANVAS_FUNCTION_NAME, CARE_PLAN_PREVIEW_FROM_CANVAS_HMAC_PATH, CARE_PLAN_STATUS, CarePlanAudits, CarePlanGenerateRuns, CarePlanMints, CarePlans, carePlanAuditSchema, carePlanGenerateEventDataSchema, carePlanGenerateRunSchema, carePlanMintSchema, carePlanPointerSchema, carePlanSchema };
//# sourceMappingURL=carePlan.mjs.map